import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
    fetchArtist,
    updateArtist,
    resetArtist,
    adminFetchGuestbook,
    adminSetStatus,
    adminDeleteEntry,
    mediaUrl,
} from "../lib/api";
import { Plus, Trash2, Save, RefreshCw, LogOut, Check, X, ImageIcon } from "lucide-react";

const ARTIST_SLUGS = ["thomas", "bernhard"];
const TABS = ["thomas", "bernhard", "guestbook"];

export default function AdminDashboard() {
    const { user, loading, signOut } = useAuth();
    const [active, setActive] = useState("thomas");
    const [data, setData] = useState(null);
    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState("");

    useEffect(() => {
        if (!user) return;
        if (active === "guestbook") return;
        setMsg("");
        setData(null);
        fetchArtist(active).then(setData).catch(() => setMsg("Laden fehlgeschlagen"));
    }, [active, user]);

    if (loading) {
        return <div className="min-h-screen flex items-center justify-center text-zinc-500">…</div>;
    }
    if (!user) return <Navigate to="/admin/login" replace />;

    if (active === "guestbook") {
        return (
            <Shell active={active} setActive={setActive} signOut={signOut}>
                <GuestbookModeration />
            </Shell>
        );
    }

    const save = async () => {
        setSaving(true);
        setMsg("");
        try {
            const updated = await updateArtist(active, data);
            setData(updated);
            setMsg("Gespeichert ✓");
        } catch (e) {
            const d = e?.response?.data?.detail;
            setMsg(typeof d === "string" ? d : "Speichern fehlgeschlagen");
        } finally {
            setSaving(false);
            setTimeout(() => setMsg(""), 2500);
        }
    };

    const reset = async () => {
        if (!window.confirm("Diesen Artist auf Standard-Inhalte zurücksetzen?")) return;
        try {
            const d = await resetArtist(active);
            setData(d);
            setMsg("Auf Standard zurückgesetzt ✓");
        } catch {
            setMsg("Reset fehlgeschlagen");
        }
        setTimeout(() => setMsg(""), 2500);
    };

    const update = (path, value) => {
        setData((prev) => {
            const next = JSON.parse(JSON.stringify(prev));
            const keys = path.split(".");
            let ref = next;
            for (let i = 0; i < keys.length - 1; i++) ref = ref[keys[i]];
            ref[keys[keys.length - 1]] = value;
            return next;
        });
    };

    if (!data) {
        return (
            <Shell active={active} setActive={setActive} signOut={signOut}>
                <div className="text-zinc-500">Lade …</div>
            </Shell>
        );
    }

    return (
        <Shell active={active} setActive={setActive} signOut={signOut}>
            <div className="flex items-center justify-between mb-8">
                <div>
                    <div className="text-xs uppercase tracking-[0.32em] text-[var(--rb-primary)]">
                        Editor
                    </div>
                    <h2 className="font-display text-3xl md:text-4xl text-white mt-1 uppercase">
                        {data.name}
                    </h2>
                </div>
                <div className="flex gap-3">
                    <button
                        onClick={reset}
                        data-testid="admin-reset"
                        className="flex items-center gap-2 px-4 py-2 border border-white/10 text-zinc-300 hover:border-[var(--rb-primary)] text-xs uppercase tracking-[0.28em]"
                    >
                        <RefreshCw className="w-4 h-4" /> Reset
                    </button>
                    <button
                        onClick={save}
                        disabled={saving}
                        data-testid="admin-save"
                        className="flex items-center gap-2 bg-[var(--rb-primary)] hover:bg-[var(--rb-primary-hover)] text-black px-5 py-2 text-xs uppercase tracking-[0.28em] disabled:opacity-60"
                    >
                        <Save className="w-4 h-4" /> Speichern
                    </button>
                </div>
            </div>

            {msg && (
                <div data-testid="admin-msg" className="mb-6 text-sm text-[var(--rb-secondary)]">
                    {msg}
                </div>
            )}

            <Field label="Name" value={data.name} onChange={(v) => update("name", v)} />
            <Field label="Untertitel" value={data.subtitle} onChange={(v) => update("subtitle", v)} />
            <Field
                label="Kurzbeschreibung"
                value={data.short_description}
                onChange={(v) => update("short_description", v)}
            />
            <Field
                label="Hero-Bild URL"
                value={data.hero_image}
                onChange={(v) => update("hero_image", v)}
            />
            <Field
                label="Biografie"
                value={data.biography}
                onChange={(v) => update("biography", v)}
                textarea
            />

            <ListField
                label="Bands & Projekte"
                items={data.bands}
                onChange={(arr) => update("bands", arr)}
                placeholder="Bandname"
                testid="bands"
            />

            <ListField
                label="Galerie (Bild-URLs)"
                items={data.gallery}
                onChange={(arr) => update("gallery", arr)}
                placeholder="https://…"
                testid="gallery"
            />

            <TimelineEditor
                blocks={data.timeline || []}
                onChange={(arr) => update("timeline", arr)}
            />

            <h3 className="font-display text-2xl text-white mt-12 mb-4 uppercase">Fan Section</h3>
            <Field
                label="Titel"
                value={data.fan_section?.title}
                onChange={(v) => update("fan_section.title", v)}
            />
            <Field
                label="Intro"
                value={data.fan_section?.intro}
                onChange={(v) => update("fan_section.intro", v)}
                textarea
            />
            <Field
                label="Interview-Link"
                value={data.fan_section?.interview_url}
                onChange={(v) => update("fan_section.interview_url", v)}
            />
            <Field
                label="Interview-Embed-URL"
                value={data.fan_section?.interview_embed}
                onChange={(v) => update("fan_section.interview_embed", v)}
            />
            <Field
                label="Lieblingssong - Titel"
                value={data.fan_section?.favorite_song_title}
                onChange={(v) => update("fan_section.favorite_song_title", v)}
            />
            <Field
                label="Lieblingssong - Link"
                value={data.fan_section?.favorite_song_url}
                onChange={(v) => update("fan_section.favorite_song_url", v)}
            />
            <Field
                label="Lieblingssong - Embed"
                value={data.fan_section?.favorite_song_embed}
                onChange={(v) => update("fan_section.favorite_song_embed", v)}
            />
            <Field
                label="Lieblingsalbum - Titel"
                value={data.fan_section?.favorite_album_title}
                onChange={(v) => update("fan_section.favorite_album_title", v)}
            />
            <Field
                label="Lieblingsalbum - Link"
                value={data.fan_section?.favorite_album_url}
                onChange={(v) => update("fan_section.favorite_album_url", v)}
            />
            <Field
                label="Lieblingsalbum - Embed"
                value={data.fan_section?.favorite_album_embed}
                onChange={(v) => update("fan_section.favorite_album_embed", v)}
            />

            <h3 className="font-display text-2xl text-white mt-12 mb-4 uppercase">Externe Links</h3>
            <LinksEditor
                items={data.links || []}
                onChange={(arr) => update("links", arr)}
            />

            <div className="h-24" />
        </Shell>
    );
}

const Shell = ({ active, setActive, signOut, children }) => (
    <div className="min-h-screen bg-[var(--rb-bg)]" data-testid="admin-dashboard">
        <header className="border-b border-white/5 sticky top-0 z-30 bg-black/60 backdrop-blur-xl">
            <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
                <Link to="/" className="font-display text-lg text-white">
                    rabitsch<span className="text-[var(--rb-primary)]">.</span>supporter — Admin
                </Link>
                <div className="flex items-center gap-2">
                    {TABS.map((s) => (
                        <button
                            key={s}
                            onClick={() => setActive(s)}
                            data-testid={`admin-tab-${s}`}
                            className={`px-4 py-2 text-xs uppercase tracking-[0.28em] border ${
                                active === s
                                    ? "border-[var(--rb-primary)] text-white"
                                    : "border-white/10 text-zinc-400 hover:text-white"
                            }`}
                        >
                            {s === "guestbook" ? "Gästebuch" : s}
                        </button>
                    ))}
                    <button
                        onClick={signOut}
                        data-testid="admin-logout"
                        className="ml-3 flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-[0.28em] text-zinc-400 hover:text-white"
                    >
                        <LogOut className="w-4 h-4" /> Logout
                    </button>
                </div>
            </div>
        </header>
        <main className="max-w-[1400px] mx-auto px-6 md:px-10 py-10">{children}</main>
    </div>
);

const Field = ({ label, value, onChange, textarea }) => (
    <div className="mb-4">
        <label className="block text-[10px] uppercase tracking-[0.32em] text-zinc-500 mb-2">
            {label}
        </label>
        {textarea ? (
            <textarea
                value={value || ""}
                onChange={(e) => onChange(e.target.value)}
                rows={5}
                className="w-full bg-black/40 border border-white/10 px-4 py-3 text-white outline-none focus:border-[var(--rb-primary)] transition"
            />
        ) : (
            <input
                value={value || ""}
                onChange={(e) => onChange(e.target.value)}
                className="w-full bg-black/40 border border-white/10 px-4 py-3 text-white outline-none focus:border-[var(--rb-primary)] transition"
            />
        )}
    </div>
);

const ListField = ({ label, items, onChange, placeholder, testid }) => {
    const update = (i, v) => {
        const next = [...items];
        next[i] = v;
        onChange(next);
    };
    const add = () => onChange([...(items || []), ""]);
    const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
    return (
        <div className="mb-6">
            <label className="block text-[10px] uppercase tracking-[0.32em] text-zinc-500 mb-3">
                {label}
            </label>
            <div className="space-y-2">
                {(items || []).map((it, i) => (
                    <div key={i} className="flex gap-2">
                        <input
                            value={it}
                            onChange={(e) => update(i, e.target.value)}
                            placeholder={placeholder}
                            data-testid={`admin-${testid}-${i}`}
                            className="flex-1 bg-black/40 border border-white/10 px-4 py-2 text-white outline-none focus:border-[var(--rb-primary)]"
                        />
                        <button
                            onClick={() => remove(i)}
                            className="px-3 border border-white/10 hover:border-[#ff6b6b] hover:text-[#ff6b6b] text-zinc-400"
                        >
                            <Trash2 className="w-4 h-4" />
                        </button>
                    </div>
                ))}
            </div>
            <button
                onClick={add}
                data-testid={`admin-${testid}-add`}
                className="mt-3 flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-[var(--rb-primary)] hover:text-[var(--rb-secondary)]"
            >
                <Plus className="w-4 h-4" /> Hinzufügen
            </button>
        </div>
    );
};

const TimelineEditor = ({ blocks, onChange }) => {
    const upd = (i, k, v) => {
        const next = [...blocks];
        next[i] = { ...next[i], [k]: v };
        onChange(next);
    };
    const updEvent = (bi, ei, v) => {
        const next = [...blocks];
        const evs = [...(next[bi].events || [])];
        evs[ei] = v;
        next[bi] = { ...next[bi], events: evs };
        onChange(next);
    };
    const addBlock = () => onChange([...(blocks || []), { decade: "Neue Dekade", events: [] }]);
    const delBlock = (i) => onChange(blocks.filter((_, idx) => idx !== i));
    const addEvent = (bi) => {
        const next = [...blocks];
        next[bi] = { ...next[bi], events: [...(next[bi].events || []), ""] };
        onChange(next);
    };
    const delEvent = (bi, ei) => {
        const next = [...blocks];
        next[bi] = { ...next[bi], events: next[bi].events.filter((_, idx) => idx !== ei) };
        onChange(next);
    };
    return (
        <div className="mt-10">
            <label className="block text-[10px] uppercase tracking-[0.32em] text-zinc-500 mb-3">
                Timeline
            </label>
            <div className="space-y-6">
                {(blocks || []).map((b, bi) => (
                    <div
                        key={bi}
                        className="border border-white/5 p-4 md:p-5 bg-black/30"
                        data-testid={`admin-timeline-${bi}`}
                    >
                        <div className="flex gap-2 mb-3">
                            <input
                                value={b.decade}
                                onChange={(e) => upd(bi, "decade", e.target.value)}
                                className="flex-1 bg-black/40 border border-white/10 px-3 py-2 text-white font-display text-lg"
                            />
                            <button
                                onClick={() => delBlock(bi)}
                                className="px-3 border border-white/10 hover:border-[#ff6b6b] hover:text-[#ff6b6b] text-zinc-400"
                            >
                                <Trash2 className="w-4 h-4" />
                            </button>
                        </div>
                        <div className="space-y-2">
                            {(b.events || []).map((ev, ei) => (
                                <div key={ei} className="flex gap-2">
                                    <input
                                        value={ev}
                                        onChange={(e) => updEvent(bi, ei, e.target.value)}
                                        className="flex-1 bg-black/40 border border-white/10 px-3 py-2 text-zinc-200 text-sm"
                                    />
                                    <button
                                        onClick={() => delEvent(bi, ei)}
                                        className="px-3 border border-white/10 hover:border-[#ff6b6b] hover:text-[#ff6b6b] text-zinc-400"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            ))}
                        </div>
                        <button
                            onClick={() => addEvent(bi)}
                            className="mt-3 flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-[var(--rb-primary)] hover:text-[var(--rb-secondary)]"
                        >
                            <Plus className="w-4 h-4" /> Event
                        </button>
                    </div>
                ))}
            </div>
            <button
                onClick={addBlock}
                data-testid="admin-timeline-add"
                className="mt-4 flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-[var(--rb-primary)] hover:text-[var(--rb-secondary)]"
            >
                <Plus className="w-4 h-4" /> Dekade hinzufügen
            </button>
        </div>
    );
};

const LinksEditor = ({ items, onChange }) => {
    const upd = (i, k, v) => {
        const next = [...items];
        next[i] = { ...next[i], [k]: v };
        onChange(next);
    };
    const add = () => onChange([...(items || []), { label: "", url: "" }]);
    const del = (i) => onChange(items.filter((_, idx) => idx !== i));
    return (
        <div className="space-y-3">
            {(items || []).map((l, i) => (
                <div key={i} className="grid grid-cols-1 md:grid-cols-[1fr_2fr_auto] gap-2">
                    <input
                        value={l.label}
                        onChange={(e) => upd(i, "label", e.target.value)}
                        placeholder="Label"
                        className="bg-black/40 border border-white/10 px-3 py-2 text-white"
                    />
                    <input
                        value={l.url}
                        onChange={(e) => upd(i, "url", e.target.value)}
                        placeholder="https://…"
                        className="bg-black/40 border border-white/10 px-3 py-2 text-white"
                    />
                    <button
                        onClick={() => del(i)}
                        className="px-3 border border-white/10 hover:border-[#ff6b6b] hover:text-[#ff6b6b] text-zinc-400"
                    >
                        <Trash2 className="w-4 h-4" />
                    </button>
                </div>
            ))}
            <button
                onClick={add}
                data-testid="admin-link-add"
                className="flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-[var(--rb-primary)] hover:text-[var(--rb-secondary)]"
            >
                <Plus className="w-4 h-4" /> Link hinzufügen
            </button>
        </div>
    );
};

const STATUS_META = {
    pending: { color: "#e5b75e", label: "🟡 Ausstehend" },
    published: { color: "#22c55e", label: "🟢 Veröffentlicht" },
    rejected: { color: "#ef4444", label: "🔴 Abgelehnt" },
};
const CAT_LABELS_ADMIN = { thomas: "Thomas", bernhard: "Bernhard", general: "Allgemein" };

const GuestbookModeration = () => {
    const [items, setItems] = useState([]);
    const [filter, setFilter] = useState("pending");
    const [loading, setLoading] = useState(true);

    const load = () => {
        setLoading(true);
        adminFetchGuestbook()
            .then(setItems)
            .finally(() => setLoading(false));
    };
    useEffect(() => {
        load();
    }, []);

    const setStatus = async (id, s) => {
        await adminSetStatus(id, s);
        load();
    };
    const remove = async (id) => {
        if (!window.confirm("Diesen Eintrag endgültig löschen?")) return;
        await adminDeleteEntry(id);
        load();
    };

    const filtered = filter === "all" ? items : items.filter((i) => i.status === filter);
    const counts = {
        all: items.length,
        pending: items.filter((i) => i.status === "pending").length,
        published: items.filter((i) => i.status === "published").length,
        rejected: items.filter((i) => i.status === "rejected").length,
    };

    return (
        <div data-testid="admin-guestbook">
            <div className="flex items-center justify-between mb-8">
                <div>
                    <div className="text-xs uppercase tracking-[0.32em] text-[var(--rb-primary)]">
                        Moderation
                    </div>
                    <h2 className="font-display text-3xl md:text-4xl text-white mt-1">
                        Gästebuch
                    </h2>
                </div>
                <div className="flex gap-2 flex-wrap">
                    {["pending", "published", "rejected", "all"].map((f) => (
                        <button
                            key={f}
                            onClick={() => setFilter(f)}
                            data-testid={`admin-gb-filter-${f}`}
                            className={`px-3 py-2 text-[10px] uppercase tracking-[0.28em] border ${
                                filter === f
                                    ? "border-[var(--rb-primary)] text-white"
                                    : "border-white/10 text-zinc-400 hover:text-white"
                            }`}
                        >
                            {f} <span className="ml-1 text-zinc-500">({counts[f]})</span>
                        </button>
                    ))}
                </div>
            </div>

            {loading ? (
                <div className="text-zinc-500">Lade …</div>
            ) : filtered.length === 0 ? (
                <div className="text-zinc-500 py-16 text-center border border-white/5">
                    Keine Einträge in dieser Kategorie.
                </div>
            ) : (
                <div className="space-y-4">
                    {filtered.map((e) => {
                        const meta = STATUS_META[e.status] || STATUS_META.pending;
                        return (
                            <div
                                key={e.id}
                                data-testid={`admin-entry-${e.id}`}
                                className="grid md:grid-cols-[110px_1fr_auto] gap-5 border border-white/5 bg-black/30 p-5"
                            >
                                <div className="w-24 h-24 md:w-[110px] md:h-[110px] bg-black/50 border border-white/5 grid place-items-center overflow-hidden">
                                    {e.photo_url ? (
                                        <img
                                            src={mediaUrl(e.photo_url)}
                                            alt=""
                                            className="w-full h-full object-cover"
                                        />
                                    ) : (
                                        <ImageIcon className="w-6 h-6 text-zinc-700" />
                                    )}
                                </div>
                                <div>
                                    <div className="flex flex-wrap items-center gap-3 mb-2">
                                        <span
                                            className="text-[10px] uppercase tracking-[0.32em]"
                                            style={{ color: meta.color }}
                                        >
                                            {meta.label}
                                        </span>
                                        <span className="text-[10px] uppercase tracking-[0.32em] text-zinc-500">
                                            {CAT_LABELS_ADMIN[e.category]}
                                        </span>
                                        {e.year && (
                                            <span className="text-[10px] uppercase tracking-[0.32em] text-zinc-500">
                                                · {e.year}
                                            </span>
                                        )}
                                        <span className="text-[10px] text-zinc-600 ml-auto">
                                            {new Date(e.created_at).toLocaleString("de-AT")}
                                        </span>
                                    </div>
                                    <div className="font-display text-lg md:text-xl text-white mb-1">
                                        {e.name}
                                    </div>
                                    <p className="text-sm text-zinc-300 whitespace-pre-wrap leading-relaxed">
                                        {e.message}
                                    </p>
                                </div>
                                <div className="flex md:flex-col gap-2 md:justify-center">
                                    {e.status !== "published" && (
                                        <button
                                            onClick={() => setStatus(e.id, "published")}
                                            data-testid={`admin-publish-${e.id}`}
                                            className="flex items-center gap-2 bg-[var(--rb-primary)] hover:bg-[var(--rb-primary-hover)] text-black px-4 py-2 text-[10px] uppercase tracking-[0.28em]"
                                        >
                                            <Check className="w-3.5 h-3.5" /> Freigeben
                                        </button>
                                    )}
                                    {e.status !== "rejected" && (
                                        <button
                                            onClick={() => setStatus(e.id, "rejected")}
                                            data-testid={`admin-reject-${e.id}`}
                                            className="flex items-center gap-2 border border-white/10 hover:border-[#ef4444] hover:text-[#ef4444] text-zinc-300 px-4 py-2 text-[10px] uppercase tracking-[0.28em]"
                                        >
                                            <X className="w-3.5 h-3.5" /> Ablehnen
                                        </button>
                                    )}
                                    <button
                                        onClick={() => remove(e.id)}
                                        data-testid={`admin-delete-${e.id}`}
                                        className="flex items-center gap-2 border border-white/10 hover:border-[#ef4444] hover:text-[#ef4444] text-zinc-500 px-4 py-2 text-[10px] uppercase tracking-[0.28em]"
                                    >
                                        <Trash2 className="w-3.5 h-3.5" /> Löschen
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

